import React, { Fragment, useEffect, useState } from "react";

import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import { putFormDataWithDotNet } from "../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../api/ajax-endpoints";

import Alert from "../../../NewComponents/TypedComponents/Alert/Alert.component";
import { errorHandler } from "../../../helpers/errorHandler";
import { appInsights } from "../../../config/appInsights";
import FundFormShell from "./FundForm/FundFormShell";
import Notice from "./FundForm/Notice";
import InvestmentDetailsSection from "./FundForm/InvestmentDetailsSection";
import VisibilitySection from "./FundForm/VisibilitySection";
import FinancialsSection from "./FundForm/FinancialsSection";
import WithdrawalWindowSection from "./FundForm/WithdrawalWindowSection";
import { needsWithdrawalWindows } from "./FundForm/planType";
import { toFundFormData } from "./FundForm/buildFundPayload";
import { confirmDiscard } from "./FundForm/confirmDiscard";
import ReviewFundModal from "./FundForm/ReviewFundModal";
import useInvestmentPlanTypes from "../../../custom-hooks/useInvestmentPlanTypes";
import {
  ACTIVE_DETAIL_LOCKS,
  ACTIVE_PRODUCT_LOCKS,
} from "./FundForm/editLocks";
import {
  TCreateInvestmentProductState,
  TInvestmentProductDetails,
} from "./CreateInvestmentProduct";

import useAnInvestmentProduct from "../../../custom-hooks/useAnInvestmentProduct";
import { handleSwalErrorAlert } from "../../../helpers";
import { hydrateProductDetail } from "./FundForm/hydrateProductDetail";
import { onChangeFile } from "../../../helpers/convertToFormData";
import { resolveProductVisibility } from "../components/ProductVisibility";

interface ILocationState {
  editType: string;
  id: number;
}

/** The two things a saved fund can be, and the only editType values. */
const EDIT_TYPES = ["drafted", "active"];

const EditInvestmentProduct = () => {
  const location = useLocation<ILocationState | undefined>();
  const { id: idFromRoute } = useParams<{ id: string }>();

  /*
   * The Investments list navigates here with `editType` in router state, which
   * a refresh throws away — the destructure then threw on `undefined` and the
   * page died with a blank screen. The query string is read as a fallback so
   * the URL is addressable, and an editType we cannot work out sends the admin
   * back to the list rather than rendering a form that submits nowhere.
   */
  const editType =
    location.state?.editType ??
    new URLSearchParams(location.search).get("editType") ??
    "";
  const id = Number(location.state?.id ?? idFromRoute);
  const isLive = editType === "active";
  const history = useHistory();
  const [activeSection, setActiveSection] = useState("investment-details");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [saveDraftError, setSaveDraftError] = useState("");
  const [state, setState] = useState<TCreateInvestmentProductState>({
    name: "",
    description: "",
    endDate: "",
    investmentProductDetails: [],
    issuers: "",
    startDate: "",
    objective: "",
    targetInvestors: "",
    investmentProductTypeId: null,
    investmentProductSubTypeId: null,
    investmentProductSubTypeDetailId: null,
    isCustomerFacing: true,
    productTypeId: null,
    withdrawalWindowInterval: null,
    withdrawalWindowIntervalUnit: null,
    gracePeriod: null,
    lockInPeriod: null,
    termSheetFile: undefined,
  });
  const [isPickingDocument, setIsPickingDocument] = useState(false);

  // Save opens the review; the review is what actually submits.
  const [isReviewing, setIsReviewing] = useState(false);

  const { data: planTypes } = useInvestmentPlanTypes();

  const { data: dataToEdit, isLoading: dataToEditIsLoading } =
    useAnInvestmentProduct(id);

  useEffect(() => {
    if (dataToEdit) {
      setState((prev) => ({
        ...prev,
        name: dataToEdit?.name,
        objective: dataToEdit?.objective,
        issuers: dataToEdit?.issuers,
        description: dataToEdit?.description,
        targetInvestors: dataToEdit?.target_investors,
        productTypeId:
          dataToEdit?.product_type_id?.value ?? dataToEdit?.product_type_id,
        investmentProductTypeId: dataToEdit?.investment_product_type_id,
        investmentProductSubTypeId: dataToEdit?.investment_product_sub_type_id,
        investmentProductSubTypeDetailId:
          dataToEdit?.investment_product_sub_type_detail_id,
        startDate: dataToEdit?.start_date?.substring(0, 10),
        endDate: dataToEdit?.end_date?.substring(0, 10),
        withdrawalWindowInterval:
          dataToEdit?.withdrawal_interval?.value ??
          dataToEdit?.withdrawal_interval ??
          null,
        withdrawalWindowIntervalUnit:
          dataToEdit?.withdrawal_interval_unit?.value ??
          dataToEdit?.withdrawal_interval_unit ??
          null,
        // Both spellings, and both shapes. The interval beside it already
        // fell back to the plain value; this one did not, so a saved Habit
        // Save fund opened with an empty grace period and saving it again
        // came back "Grace period is required".
        gracePeriod:
          dataToEdit?.grace_periods?.value ??
          dataToEdit?.grace_periods ??
          dataToEdit?.grace_period ??
          null,
        // Camel-cased HERE rather than per-render in each child. The parent
        // used to hold the API's snake_case while the children compared
        // camelCase, so `findIndex` returned -1 and an edit was written to
        // `array[-1]` — a string key on the array, silently discarded.
        investmentProductDetails: (
          dataToEdit?.investment_product_details ?? []
        ).map(hydrateProductDetail),
        isCustomerFacing: resolveProductVisibility(dataToEdit) ?? true,
      }));
    }
  }, [dataToEdit, dataToEditIsLoading]);

  const handleSubmitDraft = async () => {
    setSaveDraftError("");
    setLoading(true);

    // A drafted fund is amended in place; a live one goes for approval. The
    // body is the same either way — only the endpoint differs.
    const endpoint = `${ajaxEndpoints.FUNDER_INVESTMENT_PRODUCT}/${dataToEdit.id}/${
      isLive ? "update_active" : "update_pending"
    }`;

    try {
      const response = await putFormDataWithDotNet(
        endpoint,
        toFundFormData(state),
      );

      setSuccessMessage(response.message);
      setTimeout(() => {
        history.push("/investments");
      }, 3000);
    } catch (error: any) {
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "EditInvestmentProduct.jsx",
        },
      });

      setSaveDraftError(errorHandler(error));
    } finally {
      setLoading(false);
    }
  };

  /*  const {
    data: mifosData,
    status: mifosStatus,
    error: mifosError,
  } = useMifosProduct({
    productType : 3,
  });

  if (mifosStatus === "loading" || !state) {
    return <Loader centered={true} text="Loading mifos information..." />;
  }
  if (mifosError) {
    return (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt p-y-5">
            <i className="fas fa-bell" /> There was an issue while loading the
            mifos products, please try again.
          </h4>
        </div>
      </div>
    );
  }*/

  async function onChangeTermSheet(e: any) {
    if (isPickingDocument) return; // Prevent multiple invocations
    setIsPickingDocument(true); // Set document picking status to true
    const errorCallback = (error: any) => {
      handleSwalErrorAlert(error);
      appInsights.trackException({
        exception: new Error(error),
        properties: { fileName: "Financials.tsx" },
      });
      setIsPickingDocument(false); // Reset document picking status
    };

    const setInfoCallback = (file: File, fileName: string) => {
      setState({
        ...state,
        termSheetFile: {
          file,
          fileName,
        },
      });
      setIsPickingDocument(false); // Reset document picking status
    };

    await onChangeFile(e, setInfoCallback, errorCallback);
  }

  const updateInvestmentProductDetails = (
    updatedDetails: TInvestmentProductDetails,
  ) => {
    const updatedList = state.investmentProductDetails.map((item) =>
      item.id === updatedDetails.id ? updatedDetails : item,
    );

    setState({
      ...state,
      investmentProductDetails: updatedList,
    });
  };

  const RAIL_SECTIONS = [
    { id: "investment-details", label: "Investment details" },
    { id: "visibility", label: "Visibility & documents" },
    ...(needsWithdrawalWindows(state.productTypeId)
      ? [{ id: "withdrawal-windows", label: "Withdrawal windows" }]
      : []),
    { id: "financials", label: "Financials" },
  ];

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    document
      .getElementById(sectionId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!EDIT_TYPES.includes(editType) || !id) {
    return <Redirect to="/investments" />;
  }

  return (
    <Fragment>
      <FundFormShell
        crumb={isLive ? "Edit live fund" : "Edit draft"}
        title={state.name || "Edit fund"}
        status={{
          label: isLive ? "Active · live fund" : "Draft · not submitted",
        }}
        sections={RAIL_SECTIONS}
        activeSection={activeSection}
        onSelectSection={scrollToSection}
        railNote={
          isLive
            ? "Changes to a live fund go for approval before they take effect."
            : "A draft is not visible to customers until an approver activates it."
        }
        onCancel={() =>
          confirmDiscard(() => history.push("/investments"), true)
        }
        onSubmit={() => setIsReviewing(true)}
        submitLabel={isLive ? "Submit for approval" : "Update"}
        submitting={loading}
      >
        {saveDraftError ? <Alert message={saveDraftError} /> : null}
        {successMessage ? (
          <Alert message={successMessage} type="success" />
        ) : null}

        {isLive ? (
          <Notice tone="purple">
            <strong style={{ fontWeight: 500 }}>
              Some fields are locked once a fund is live.
            </strong>{" "}
            Customers have already subscribed against them, so changing them
            would rewrite the terms of an existing investment. Everything still
            editable is shown normally.
          </Notice>
        ) : null}

        <InvestmentDetailsSection
          step={`Step 1 of ${RAIL_SECTIONS.length}`}
          locks={isLive ? ACTIVE_PRODUCT_LOCKS : {}}
          value={{
            name: state.name,
            productTypeId: state.productTypeId,
            investmentProductTypeId: state.investmentProductTypeId,
            investmentProductSubTypeId: state.investmentProductSubTypeId,
            investmentProductSubTypeDetailId:
              state.investmentProductSubTypeDetailId,
            startDate: state.startDate,
            endDate: state.endDate,
            description: state.description,
            objective: state.objective,
            issuers: state.issuers,
            targetInvestors: state.targetInvestors,
          }}
          onChange={(patch) => setState((prev) => ({ ...prev, ...patch }))}
        />

        <VisibilitySection
          step={`Step 2 of ${RAIL_SECTIONS.length}`}
          isCustomerFacing={state.isCustomerFacing}
          onVisibilityChange={(isCustomerFacing) =>
            setState((prev) => ({ ...prev, isCustomerFacing }))
          }
          termSheetName={state.termSheetFile?.fileName}
          onPickTermSheet={onChangeTermSheet}
        />

        {needsWithdrawalWindows(state.productTypeId) ? (
          <WithdrawalWindowSection
            step={`Step 3 of ${RAIL_SECTIONS.length}`}
            value={{
              interval: state.withdrawalWindowInterval,
              unit: state.withdrawalWindowIntervalUnit,
              gracePeriod: state.gracePeriod,
              lockInPeriod: state.lockInPeriod,
            }}
            isEditing
            onChange={(next) =>
              setState((prev) => ({
                ...prev,
                withdrawalWindowInterval: next.interval ?? null,
                withdrawalWindowIntervalUnit: next.unit ?? null,
                gracePeriod: next.gracePeriod ?? null,
                lockInPeriod: next.lockInPeriod ?? null,
              }))
            }
          />
        ) : null}

        <FinancialsSection
          step={`Step ${RAIL_SECTIONS.length} of ${RAIL_SECTIONS.length}`}
          details={state.investmentProductDetails}
          locks={ACTIVE_DETAIL_LOCKS}
          isLive={isLive}
          onChangeDetail={(detail, patch) =>
            updateInvestmentProductDetails({ ...detail, ...patch })
          }
        />
      </FundFormShell>

      <ReviewFundModal
        open={isReviewing}
        state={state}
        planTypeLabel={
          planTypes?.find((type) => type.id === Number(state.productTypeId))
            ?.description
        }
        submitLabel={isLive ? "Submit for approval" : "Save changes"}
        submitting={loading}
        footerNote={
          isLive
            ? "These changes go to an approver before they take effect."
            : "Nothing is saved until you confirm."
        }
        onClose={() => setIsReviewing(false)}
        onGoToSection={scrollToSection}
        onConfirm={() => {
          setIsReviewing(false);
          handleSubmitDraft();
        }}
      />
    </Fragment>
  );
};

export default EditInvestmentProduct;
